import { useEffect, useState } from "react";
import { X, Copy, Check, Eye, EyeOff, Share2, Loader2, Trash2, Image as ImageIcon, ImageOff, CalendarDays, CalendarX2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ShareMealsToggle } from "@/components/ShareMealsToggle";
import { useI18n } from "@/lib/i18n.jsx";

/**
 * ShareProgressModal — lets the owner create/update a coach-shareable link.
 */
export default function ShareProgressModal({ open, onClose }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [existing, setExisting] = useState(null); // {share_token, include_photos, view_count, ...}
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [includePhotos, setIncludePhotos] = useState(true);
  // Server truth for the photos toggle so the pill never lies. Only
  // updated after a successful PATCH round-trip.
  const [savedPhotos, setSavedPhotos] = useState(true);
  const [photosSaving, setPhotosSaving] = useState(false);
  // Same pattern for the calendar (planner + journal) toggle.
  const [includeCalendar, setIncludeCalendar] = useState(true);
  const [savedCalendar, setSavedCalendar] = useState(true);
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [includeMeals, setIncludeMeals] = useState(true);
  const [copied, setCopied]     = useState(false);
  const [err, setErr]           = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setErr("");
    setCopied(false);
    setPassword("");
    api.get("/share/progress")
      .then(({ data }) => {
        if (data?.exists) {
          setExisting(data);
          setIncludePhotos(!!data.include_photos);
          setSavedPhotos(!!data.include_photos);
          const calendarOn = data.include_calendar !== false; // default true
          setIncludeCalendar(calendarOn);
          setSavedCalendar(calendarOn);
          setIncludeMeals(data.include_meals !== false);
        } else {
          setIncludeMeals(true);
          setExisting(null);
          setIncludePhotos(true);
          setSavedPhotos(true);
          setIncludeCalendar(true);
          setSavedCalendar(true);
        }
      })
      .catch(() => setErr(en ? "Couldn't load share settings" : "تعذّر تحميل إعدادات المشاركة"))
      .finally(() => setLoading(false));
  }, [open, en]);

  if (!open) return null;

  const shareUrl = existing?.share_token
    ? `${window.location.origin}/share/progress/${existing.share_token}`
    : "";

  const canSave = password.length >= 4;

  async function handleSave() {
    if (!canSave) { setErr(en ? "Password must be at least 4 characters" : "كلمة السر يجب أن تكون 4 أحرف على الأقل"); return; }
    setSaving(true); setErr("");
    try {
      const { data } = await api.post("/share/progress", {
        password,
        include_photos: includePhotos,
        include_calendar: includeCalendar,
        include_meals: includeMeals,
      });
      setExisting({ ...data, view_count: existing?.view_count || 0 });
      setSavedPhotos(!!data.include_photos);
      setSavedCalendar(data.include_calendar !== false);
      setPassword("");
      toast.success(existing ? (en ? "Share updated" : "تم تحديث المشاركة") : (en ? "Link created" : "تم إنشاء الرابط"));
    } catch (e) {
      setErr(en ? "Couldn't save settings" : "تعذّر حفظ الإعدادات");
    } finally { setSaving(false); }
  }

  async function togglePhotos(nextValue) {
    setIncludePhotos(nextValue);
    if (!existing) return;
    setPhotosSaving(true);
    try {
      await api.patch("/share/progress/photos", { include_photos: nextValue });
      setSavedPhotos(nextValue);
      toast.success(nextValue
        ? (en ? "Photos are now visible to your coach" : "الصور مرئية للكوتش الآن")
        : (en ? "Photos are hidden from your coach" : "تم إخفاء الصور عن الكوتش"));
    } catch (e) {
      toast.error(en ? "Couldn't save the change" : "تعذّر حفظ التغيير");
      setIncludePhotos(savedPhotos);
    } finally { setPhotosSaving(false); }
  }

  async function toggleCalendar(nextValue) {
    setIncludeCalendar(nextValue);
    if (!existing) return;
    setCalendarSaving(true);
    try {
      await api.patch("/share/progress/calendar", { include_calendar: nextValue });
      setSavedCalendar(nextValue);
      toast.success(nextValue
        ? (en ? "Calendar is now visible to your coach" : "التقويم مرئي للكوتش الآن")
        : (en ? "Calendar is hidden from your coach" : "تم إخفاء التقويم عن الكوتش"));
    } catch (e) {
      toast.error(en ? "Couldn't save the change" : "تعذّر حفظ التغيير");
      setIncludeCalendar(savedCalendar);
    } finally { setCalendarSaving(false); }
  }

  async function handleRevoke() {
    if (!window.confirm(en ? "Revoke the share? Your coach won't have access anymore." : "إلغاء المشاركة؟ الكوتش لن يستطيع الوصول بعد الآن.")) return;
    setSaving(true);
    try {
      await api.delete("/share/progress");
      setExisting(null);
      setPassword("");
      setSavedPhotos(true);
      setIncludePhotos(true);
      setSavedCalendar(true);
      setIncludeCalendar(true);
      toast.success(en ? "Share revoked" : "تم إلغاء المشاركة");
    } finally { setSaving(false); }
  }

  function handleCopy() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const photosDirty   = existing && includePhotos   !== savedPhotos;
  const calendarDirty = existing && includeCalendar !== savedCalendar;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto border-[1.5px] border-black"
           dir={en ? "ltr" : "rtl"} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-brand-600" />
            <h3 className="font-black text-lg">{en ? "Share my progress" : "مشاركة تقدمي"}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-neutral-100" data-testid="share-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-500" /></div>
        ) : (
          <>
            {existing && (
              <div className="bg-emerald-50 rounded-2xl p-3 space-y-2 border border-emerald-100">
                <div className="text-[11px] text-emerald-800 font-bold">{en ? "Share link is active" : "رابط المشاركة نشط"}</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 text-[11px] text-neutral-700 bg-white rounded-lg px-2 py-1.5 truncate num" dir="ltr">
                    {shareUrl}
                  </div>
                  <button onClick={handleCopy}
                          className="rounded-lg bg-brand-500 text-white px-3 py-1.5 text-xs font-bold flex items-center gap-1 active:scale-95"
                          data-testid="share-copy-btn">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? (en ? "Copied" : "نُسخ") : (en ? "Copy" : "نسخ")}
                  </button>
                </div>
                <div className="text-[10px] text-neutral-500">
                  {en ? `Viewed ${existing.view_count || 0} time${(existing.view_count || 0) === 1 ? "" : "s"}` : `شوهد ${existing.view_count || 0} مرة`}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="text-[11px] font-bold text-neutral-700">
                {existing
                  ? (en ? "Update password (optional):" : "تحديث كلمة السر (اختياري):")
                  : (en ? "Password for your coach:" : "كلمة سر للكوتش:")}
              </div>
              <div className="flex gap-2 items-stretch">
                <div className="flex-1 relative">
                  <input type={showPw ? "text" : "password"} value={password}
                         onChange={(e) => setPassword(e.target.value)}
                         placeholder={en ? "At least 4 characters" : "4 أحرف على الأقل"}
                         className="w-full bg-neutral-50 rounded-xl px-3 py-2.5 text-sm outline-none border-[1.5px] border-neutral-200 focus:border-brand-400"
                         dir="ltr"
                         data-testid="share-password-input" />
                  <button onClick={() => setShowPw((v) => !v)} type="button"
                          className={`absolute inset-y-0 ${en ? "right-2" : "left-2"} flex items-center text-neutral-400`}>
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-neutral-50 rounded-2xl p-3 border-[1.5px] border-neutral-100">
              <label className="flex items-center justify-between gap-2 cursor-pointer">
                <div className="flex items-center gap-2 min-w-0">
                  {savedPhotos ? <ImageIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                               : <ImageOff  className="w-4 h-4 text-neutral-400 flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-neutral-800">{en ? "Share photos" : "مشاركة الصور"}</div>
                    <div className="text-[10px] text-neutral-500">{en ? "Toggle saves instantly — no update button needed" : "التبديل يُحفظ فوراً — لا تحتاج زر تحديث"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {photosSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin text-brand-500" data-testid="share-photos-saving" />
                  ) : (
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        savedPhotos
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-neutral-200 text-neutral-600"
                      }`}
                      data-testid="share-photos-status-pill"
                    >
                      {savedPhotos
                        ? (en ? "Visible to coach" : "مرئية للكوتش")
                        : (en ? "Hidden" : "مخفية")}
                    </span>
                  )}
                  <input type="checkbox" checked={includePhotos}
                         onChange={(e) => togglePhotos(e.target.checked)}
                         disabled={photosSaving}
                         className="w-5 h-5 accent-brand-500"
                         data-testid="share-include-photos-toggle" />
                </div>
              </label>
              {photosDirty && (
                <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1 text-center">
                  {en ? "Saving..." : "جاري الحفظ..."}
                </div>
              )}
            </div>

            {/* Calendar (planner + journal) toggle — mirrors the photos row
                so the trainee has the same UX for hiding tasks & notes. */}
            <div className="bg-neutral-50 rounded-2xl p-3 border-[1.5px] border-neutral-100">
              <label className="flex items-center justify-between gap-2 cursor-pointer">
                <div className="flex items-center gap-2 min-w-0">
                  {savedCalendar ? <CalendarDays className="w-4 h-4 text-sky-600 flex-shrink-0" />
                                 : <CalendarX2   className="w-4 h-4 text-neutral-400 flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-neutral-800">{en ? "Share calendar" : "مشاركة التقويم"}</div>
                    <div className="text-[10px] text-neutral-500">
                      {en
                        ? "Tasks + journal entries — read-only for your coach"
                        : "المهام واليوميات — عرض للاطلاع فقط من قِبل الكوتش"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {calendarSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin text-sky-500" data-testid="share-calendar-saving" />
                  ) : (
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        savedCalendar
                          ? "bg-sky-100 text-sky-700"
                          : "bg-neutral-200 text-neutral-600"
                      }`}
                      data-testid="share-calendar-status-pill"
                    >
                      {savedCalendar
                        ? (en ? "Visible to coach" : "مرئي للكوتش")
                        : (en ? "Hidden" : "مخفي")}
                    </span>
                  )}
                  <input type="checkbox" checked={includeCalendar}
                         onChange={(e) => toggleCalendar(e.target.checked)}
                         disabled={calendarSaving}
                         className="w-5 h-5 accent-sky-500"
                         data-testid="share-include-calendar-toggle" />
                </div>
              </label>
              {calendarDirty && (
                <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1 text-center">
                  {en ? "Saving..." : "جاري الحفظ..."}
                </div>
              )}
            </div>

            <ShareMealsToggle en={en} value={includeMeals} onChange={setIncludeMeals} hasShare={!!existing} />

            {err && <div className="text-[11px] text-red-600 text-center">{err}</div>}

            <div className="flex gap-2 pt-1">
              <button onClick={handleSave} disabled={!canSave || saving}
                      className="flex-1 rounded-2xl py-3 bg-brand-500 text-white font-bold text-sm disabled:opacity-40 active:scale-98"
                      data-testid="share-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> :
                 (existing
                   ? (en ? "Update password" : "تحديث كلمة السر")
                   : (en ? "Create link" : "إنشاء الرابط"))}
              </button>
              {existing && (
                <button onClick={handleRevoke} disabled={saving}
                        className="w-11 rounded-2xl bg-red-50 text-red-600 border-[1.5px] border-red-200 flex items-center justify-center active:scale-95"
                        data-testid="share-revoke-btn"
                        aria-label={en ? "Revoke share" : "إلغاء المشاركة"}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
